import { TRACK_DOMAIN, TRACK_ID } from "./constants"
import { RailyticsActionParameters, RailyticsBaseParameters, RailyticsConfiguration, RailyticsMediaParameters, RailyticsPageParameters, RailyticsParameters } from "./models"
import { baseparams } from "./parameters"
import { getTimezone, merge } from "./utilities"

type SendFn = (type: 'page' | 'action' | 'media', params: Record<string, string>) => Promise<void>

const version = '0.0.1';

const paramsMapping = {
  contentGroup: ['cg', [] as string[]],
  customParameter: ['cp', [] as string[]],
  internalSearch: ['is', ''],
  birthday: ['uc707', ''],
  customerId: ['cd', ''],
  gender: ['uc706', ''],
  urmCategory: ['uc', [] as string[]],
  linkId: ['ct', ''],
  customClickParameter: ['ck', [] as string[]],
  campaignId: ['mc', ''],
  campaignAction: ['mca', ''],
}

type ParamsMapping = typeof paramsMapping

export type ParamsMappingData = {
  [Key in keyof ParamsMapping]?: ParamsMapping[Key][1] extends string ? string : string[]
}

function getParams<K extends keyof ParamsMapping>(key: K, value: ParamsMappingData[K]) {
  const resp: { [key: string]: string } = {}
  const [prefix, type] = paramsMapping[key] as [string, string | string[]]
  if (typeof type === 'string') {
    resp[prefix] = typeof value === 'string' || typeof value === 'undefined' ? (value ?? '') : value.join(',')
  } else if (typeof value === 'string' || typeof value === 'undefined') {
    resp[`${prefix}1`] = value ?? ''
  } else {
    value.forEach((val, idx) => {
      resp[`${prefix}${idx}`] = val
    })
  }
  return resp
}

export class Railytics {
  constructor (private sendFn: SendFn, config: Partial<RailyticsConfiguration> = {}) {
    const globalConfig = window.railyticsConfiguration ?? {}
    const defaultConfig: RailyticsConfiguration = {
      contentId: '',
      cookie: '3',
      forceHTTPS: true,
      tabBrowsing: false,
      mediaCode: '',
      trackId: TRACK_ID,
      trackDomain: TRACK_DOMAIN,
      safetag: {
        async: true,
        timeout: 2000,
        safetagDomain: TRACK_DOMAIN,
        safetagId: TRACK_ID,
      }
    }
    this.config = merge(defaultConfig, globalConfig, config)

    this.baseSendParameters = {
      eor: '1',
      tz: `${getTimezone()}`,
      la: navigator.language.substring(0, 2),
      pu: document.location.href.split('#')[0],
    };
  }

  declare config: RailyticsParameters
  declare baseSendParameters: Pick<RailyticsBaseParameters, 'eor' | 'tz' | 'la' | 'pu' >;

  private sentFullPixel: string | undefined = undefined;

  private sentCampaignIds: Record<string, true> = {};

  public sendInfo(params: ParamsMappingData, contentId?: string, type: 'page' | 'click' | 'link' = 'page') {
    if (params.linkId) {
      type = 'click'
      if (!contentId) {
        contentId = params.linkId
      }
    }
    this.send(contentId, type, params);
  }

  public send(contentId?: string, type: 'page' | 'click' | 'link' = 'page', params: ParamsMappingData = {}) {
    if (type === 'link' || type === 'click' && !params.linkId) { params.linkId = contentId }
    if (!contentId) { contentId = this.config.contentId ? this.config.contentId : 'no_content' }
    if (type === 'page') {
      const sendParams: RailyticsPageParameters = {
        ...this.baseSendParameters,
        p: version.replaceAll('.', '') + ',' + contentId + ',1,' + baseparams(this.config, this.sentFullPixel),
        ...getParams('customParameter', params.customParameter),
        ...getParams('customerId', params.customerId),
        ...getParams('urmCategory', params.urmCategory),
        ...getParams('gender', params.gender),
        ...getParams('birthday', params.birthday?.replaceAll(/[-/]/g, '')),
        ...getParams('contentGroup', params.contentGroup),
        ...getParams('internalSearch', params.internalSearch),
        np: '',
      }
      if (params.campaignId) {
        const mca = params.campaignAction?.substring(0, 1) ?? 'c';
        const _ = mca + '_' + params.campaignId;
        let campaignId = params.campaignId;
        if (_ in this.sentCampaignIds) {
          campaignId = 'ignore%3Dignore';
        } else {
          this.sentCampaignIds[_] = true;
        }
        sendParams.mc = campaignId;
        sendParams.mca = mca
      }
      this.quicksend(sendParams, 'page')
    } else {
      const sendParams: RailyticsActionParameters = {
        ...this.baseSendParameters,
        p: version.replaceAll('.', '') + ',' + contentId + ',1,' + baseparams(this.config, this.sentFullPixel ),
        ct: params.linkId ?? '',
        ...getParams('customClickParameter', params.customClickParameter),
      }
      this.quicksend(sendParams, 'action')
    }
  }

  private quicksend(params: RailyticsActionParameters, type: 'action'): void;
  private quicksend(params: RailyticsPageParameters, type: 'page'): void;
  private quicksend(params: RailyticsActionParameters | RailyticsPageParameters, type: 'action' | 'page'): void {
    void this.sendFn(type, params as unknown as Record<string, string>)
    this.sentFullPixel = '2';
  }

  public sendInfoMedia(mediaId: string, ev: string, currentTime: string, duration: string, media_parameter: string, bandwidth: string, volume: string, muted: string, idCusv?: string, idCreativita?: string, idCampagna?: string, idLineItem?: string) {
    const params: RailyticsMediaParameters = {
      p: version.replaceAll('.', '') + ',st,1,' + baseparams(this.config, '1'),
      bw: bandwidth || '',
      vol: volume || '',
      mut: muted || '',
      x: new Date().getTime().toString(),
      mi: mediaId,
      mk: ev,
      mt1: currentTime,
      mt2: duration,
      /* parametri aggiuntivi che vengono passati solo per gli spot pubblicitari */
      p_id: idCusv,
      cr_id: idCreativita,
      ca_id: idCampagna,
      l_id: idLineItem
    }
    if (media_parameter) {
      const mp = media_parameter.split(';')
      for (const m of mp) {
        const [key, val] = m.split('=');
        if (ev === 'init' || ev === 'play' || !key.includes('mg')) {
          params[key] = decodeURIComponent(val)
        }
      }
    }

    this.quickSendMedia(
      this.config.trackId,
      params
    )
  }

  private quickSendMedia(_trackId: string, params: RailyticsMediaParameters) {
    void this.sendFn('media', params as unknown as Record<string, string>)
  }
}
